<template>
    <div class="week" :class="zoomClass" :style="weekStyle">
        <div class="week-info">
            <span class="week-number">{{ weekNumber }}</span>
            {{ weekInfo }}
        </div>
        <div class="days">
            <div
                class="date"
                :class="{ 'planning-today': day.formatted == formatDate(today) }"
                :style="dateStyle"
                v-for="day in days"
                :key="day.formatted"
            >
                {{ getDate(day.localDate) }}
            </div>
        </div>
    </div>
</template>

<script>
import {
    convert,
    DateTimeFormatter,
    DayOfWeek,
    TemporalAdjusters,
} from '@js-joda/core';

export default {
    data() {
        return {
            today: new Date(),
        };
    },
    props: {
        week: {
            type: Object,
            required: true,
        },
        calendarWidth: {
            type: Number,
            default: 0,
        },
        currentZoom: {
            type: Number,
            default: 1,
        },
    },
    computed: {
        zoomClass() {
            return 'zoom-' + this.currentZoom;
        },
        dateStyle() {
            if (this.currentZoom === 0) {
                // Day view: fixed width based on calendarWidth
                const width = this.calendarWidth || window.innerWidth - 40;
                return {
                    flex: `0 0 ${width}px`,
                    minWidth: `${width}px`,
                };
            }
            if (this.currentZoom === 2) {
                // Month view: dates expand within week, 60px minimum
                return {
                    minWidth: '60px',
                };
            }
            // Week view: flex expand with min-width
            return {
                minWidth: '180px',
            };
        },
        weekMinWidth() {
            switch (this.currentZoom) {
                case 0: return null; // Handled by CSS
                case 1: return '1260px';
                case 2: return '420px';
                default: return '1260px';
            }
        },
        weekStyle() {
            if (this.currentZoom === 2) {
                // Month view: 4 weeks fit in viewport
                const weekWidth = this.calendarWidth ? this.calendarWidth / 4 : null;
                return {
                    width: weekWidth ? `${weekWidth}px` : '25%',
                    minWidth: this.weekMinWidth,
                };
            }
            // Day/Week view: 1 week fills viewport
            return {
                width: this.calendarWidth ? `${this.calendarWidth}px` : '100%',
                minWidth: this.weekMinWidth,
            };
        },
        days() {
            const dateformatter = DateTimeFormatter.ofPattern('yyyy-MM-dd');

            // Day view: each "week" is actually just one day
            if (this.currentZoom === 0) {
                return [{
                    localDate: this.week,
                    formatted: this.week.format(dateformatter),
                }];
            }

            // Week and month view: return all days of the week
            let days = [];
            let startOfWeek = this.week;
            const endOfWeek = startOfWeek.with(TemporalAdjusters.nextOrSame(DayOfWeek.SUNDAY));

            days.push({
                localDate: startOfWeek,
                formatted: startOfWeek.format(dateformatter),
            });
            while (startOfWeek.isBefore(endOfWeek)) {
                startOfWeek = startOfWeek.plusDays(1);
                days.push({
                    localDate: startOfWeek,
                    formatted: startOfWeek.format(dateformatter),
                });
            }
            return days;
        },
        weekInfo() {
            // Day view: show full date info for the single day
            if (this.currentZoom === 0) {
                const jsDate = convert(this.week).toDate();
                let options = { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' };
                return jsDate.toLocaleDateString(window.locale, options);
            }

            // Week and month view: show month/year range
            const startOfWeek = this.week;
            const endOfWeek = startOfWeek.with(TemporalAdjusters.nextOrSame(DayOfWeek.SUNDAY));

            if (startOfWeek.monthValue() === endOfWeek.monthValue()) {
                let options = { month: 'long', year: 'numeric' };
                const jsDate = convert(startOfWeek).toDate();
                return jsDate.toLocaleDateString(window.locale, options);
            }

            let options = { month: 'short' };
            const startJsDate = convert(startOfWeek).toDate();
            const startMonth = startJsDate.toLocaleDateString(window.locale, options);

            options = { month: 'short', year: 'numeric' };
            const endJsDate = convert(endOfWeek).toDate();
            const endMonth = endJsDate.toLocaleDateString(window.locale, options);

            return startMonth + ' - ' + endMonth;
        },
        weekNumber() {
            // Day view: don't show week number (day is shown in weekInfo)
            if (this.currentZoom === 0) {
                return '';
            }
            return 'W' + this.week.isoWeekOfWeekyear();
        },
    },
    methods: {
        getDate(date) {
            const jsDate = convert(date).toDate();
            let options = { weekday: 'short', day: 'numeric' };
            return jsDate.toLocaleDateString(window.locale, options);
        },
        formatDate(date) {
            const d = new Date(date);
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return [year, month, day].join('-');
        },
    },
};
</script>

<style lang="scss" scoped>
.week {
    flex: none;
    box-sizing: border-box;
    line-height: 25px;
    text-transform: lowercase;
    background: #fff;
    border-left: 1px solid #6e7c7c;
    border-right: 1px solid #6e7c7c;
    margin-left: -1px;
    margin-right: -1px;
    &:last-child {
        border-right: 0;
    }
}
.week-info {
    display: block;
    width: 100%;
    box-sizing: border-box;
    text-align: center;
    position: relative;
    line-height: 25px;
    padding: 0;
    border-bottom: 1px solid #6e7c7c;
}
.week-number {
    position: absolute;
    top: 0;
    left: 10px;
    text-transform: uppercase;
}
.days {
    display: flex;
    width: 100%;
    box-sizing: border-box;
}
.date {
    flex: 1;
    box-sizing: border-box;
    border-right: 1px solid #ccc;
    text-align: center;
    overflow: hidden;
    white-space: nowrap;
    line-height: 25px;
    padding: 0;
    &:last-child {
        border-right: 0;
    }
}

// Zoom level specific styles
// Day view (zoom-0): date sizing handled by inline dateStyle
.week.zoom-0 {
    flex: 0 0 auto;

    // Hide the days row in day view since date is shown in week-info
    .days {
        display: none;
    }
}

// Week view (zoom-1) and Month view (zoom-2):
// date sizing handled by inline dateStyle, week sizing by weekStyle
</style>
